import { FC, KeyboardEvent, ReactNode, useEffect, useRef, useState } from "react";

import { Paper, Stack } from "@mui/material";
import { drawerClasses } from "@mui/material/Drawer";

import { PanelFooter } from "./PanelFooter";
import { PanelHeader } from "./PanelHeader";

import type { AssistantFeature } from "../../contexts/AssistantConfigContext";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { DashboardDrawer } from "@/dashboard-design-system/Drawer/DashboardDrawer";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

interface PanelProps {
  feature: AssistantFeature;
  isOpen: boolean;
  isHidden?: boolean;
  isStartNewDisabled: boolean;
  onStartNew?: () => void;
  onClose: () => void;
  children: ReactNode;
  details?: ReactNode;
  onCloseDetails?: () => void;
}

export const Panel: FC<PanelProps> = (props) => {
  const { isSmallScreen, value: prefersReducedMotion } = useMediaQuery(REDUCED_MOTION_QUERY);
  const { isOpen, isHidden = false, onClose } = props;
  const isShown = useOpenAfterMount(isOpen);
  const content = <Content {...props} />;

  if (isSmallScreen) {
    return (
      <DashboardDrawer
        open={isShown && !isHidden}
        anchor="bottom"
        keepMounted
        noPadding
        noDivider
        scrollableContent
        height="100dvh"
        transitionDuration={prefersReducedMotion || isHidden ? 0 : undefined}
        slots={{ header: <></> }}
        sx={{ [`& .${drawerClasses.paper}.${drawerClasses.paperAnchorBottom}`]: { borderRadius: 0 } }}
        onClose={onClose}
      >
        {content}
      </DashboardDrawer>
    );
  }

  return (
    <Paper
      elevation={0}
      aria-hidden={isHidden || undefined}
      inert={isHidden || undefined}
      sx={(theme) => ({
        visibility: isShown && !isHidden ? "visible" : "hidden",
        transform: isShown ? "none" : `translateY(calc(100% + ${theme.spacing(2)}))`,
        transition:
          prefersReducedMotion || isHidden
            ? "none"
            : theme.transitions.create(["transform", "visibility"], {
                duration: isShown
                  ? theme.transitions.duration.enteringScreen
                  : theme.transitions.duration.leavingScreen,
                easing: isShown ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
              }),
        position: "fixed",
        bottom: theme.spacing(2),
        right: theme.spacing(2),
        zIndex: theme.zIndex.hubspot - 1,
        width: theme.spacing(55),
        maxWidth: `calc(100vw - ${theme.spacing(4)})`,
        height: `min(${theme.spacing(82)}, calc(100dvh - ${theme.spacing(4)}))`,
        borderRadius: theme.radius(2),
        boxShadow: theme.boxShadow.base.strong,
        overflow: "hidden",
      })}
    >
      {content}
    </Paper>
  );
};

const Content: FC<PanelProps> = ({
  feature,
  isOpen,
  isHidden = false,
  isStartNewDisabled,
  onStartNew,
  onClose,
  children,
  details,
  onCloseDetails,
}) => {
  const container = useRef<HTMLDivElement>(null);
  const focusedComposer = useRef<HTMLElement | null>(null);
  const hasDetails = Boolean(details);
  const titleId = `${feature}-panel-title`;

  useEffect(() => {
    const root = container.current;
    if (!root) {
      return;
    }
    const keepFocusInPanel = (event: FocusEvent) => event.stopPropagation();
    root.addEventListener("focusin", keepFocusInPanel);
    return () => root.removeEventListener("focusin", keepFocusInPanel);
  }, []);

  useEffect(() => {
    const focused = document.activeElement;
    if (isHidden && focused instanceof HTMLElement && container.current?.contains(focused)) {
      focused.blur();
    }
  }, [isHidden]);

  useEffect(() => {
    const root = container.current;
    if (!root || !isOpen || hasDetails || isHidden) {
      return;
    }
    focusedComposer.current = null;
    const focusComposer = () => {
      const composer = root.querySelector<HTMLElement>(`[data-test="${feature}-input"]`);
      if (composer && composer !== focusedComposer.current) {
        focusedComposer.current = composer;
        composer.focus();
      }
    };
    focusComposer();
    const observer = new MutationObserver(focusComposer);
    observer.observe(root, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isOpen, hasDetails, isHidden, feature]);

  return (
    <Stack
      ref={container}
      role="dialog"
      aria-labelledby={titleId}
      data-test={`${feature}-panel`}
      onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") {
          event.stopPropagation();
          (hasDetails && onCloseDetails ? onCloseDetails : onClose)();
        }
      }}
      sx={(theme) => ({
        position: "relative",
        height: "100%",
        minHeight: 0,
        bgcolor: theme.palette.surface.neutral.white,
      })}
    >
      <Stack inert={hasDetails} sx={{ flex: 1, minHeight: 0 }}>
        <PanelHeader
          feature={feature}
          titleId={titleId}
          isStartNewDisabled={isStartNewDisabled}
          onStartNew={onStartNew}
          onClose={onClose}
        />
        {children}
        <PanelFooter />
      </Stack>
      {hasDetails && <Stack sx={{ position: "absolute", inset: 0, zIndex: 1 }}>{details}</Stack>}
    </Stack>
  );
};

const useOpenAfterMount = (isOpen: boolean): boolean => {
  const [isShown, setIsShown] = useState(false);
  useEffect(() => {
    setIsShown(isOpen);
  }, [isOpen]);
  return isShown;
};
